import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { useNotifications } from '@sequencehq/core-components'
import { useMutation } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'

import { useCurrentUser } from 'common/hooks'
import { automationsPaths } from 'Automations/router'
import { InvoiceUpdateReviewOutcome } from '../types'

/**
 * Dismisses an invoice-update review task (the `DISMISSED` outcome): nothing is
 * applied and the task leaves the inbox — the same completion the review modal's
 * "Dismiss" action performs. Exposes `isPending` from the mutation so the trigger
 * can show its own loading state without owning any local state.
 */
export const useDismissInvoiceUpdateReviewTask = ({
  taskId,
  invoiceLabel
}: {
  taskId: string
  invoiceLabel: string
}) => {
  const { displayNotification } = useNotifications()
  const navigate = useNavigate()
  const currentUser = useCurrentUser()

  const dismissMutation = useMutation(
    dashboardv99990101Client.postCompleteManualTask
  )

  const dismiss = useCallback(async () => {
    const dismissOutcome: InvoiceUpdateReviewOutcome = { outcome: 'DISMISSED' }
    try {
      await dismissMutation.mutateAsync({
        id: taskId,
        outputData: { ...dismissOutcome, completedBy: currentUser.id }
      })
      displayNotification(`Dismissed proposed changes to ${invoiceLabel}`, {
        type: 'success'
      })
      navigate(automationsPaths.manualTasksInbox())
    } catch {
      displayNotification('Failed to dismiss task', { type: 'error' })
    }
  }, [
    dismissMutation,
    taskId,
    currentUser.id,
    invoiceLabel,
    displayNotification,
    navigate
  ])

  return { dismiss, isPending: dismissMutation.isPending }
}
